import { type DehydratedState } from '@tanstack/react-query';

import { componentTypeNames } from '~/routes/$/page-template/component-map.types';
import { type ComponentStateType, type SectionMap } from '~/types/template';
import { invalidDehydratedState } from '~/utils/invalidDehydratedState';

const COUPON_COMPONENT_NAMES = [componentTypeNames.MATCHES_COUPON, componentTypeNames.OUTRIGHTS_COUPON] as const;

type CouponPayload = {
  outrightMarkets?: unknown[];
  subevents?: { type?: string }[];
};

const payloadHasOdds = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const { outrightMarkets, subevents } = data as CouponPayload;
  return Boolean(subevents?.some(subevent => subevent.type === 'MATCH')) || Boolean(outrightMarkets?.length);
};

const couponHasNoOdds = (component: ComponentStateType): boolean => {
  const state = component.componentState;
  if (invalidDehydratedState(state)) return true;
  const queries = (state as DehydratedState | undefined)?.queries ?? [];
  return queries.every(query => !payloadHasOdds(query.state.data));
};

export const allCouponsAreEmpty = (sections: SectionMap): boolean => {
  const coupons = Object.values(sections)
    .flatMap(components => components ?? [])
    .filter(component => COUPON_COMPONENT_NAMES.includes(component.name));

  return coupons.every(couponHasNoOdds);
};

export const withoutCoupons = (sections: SectionMap): SectionMap =>
  Object.fromEntries(
    Object.entries(sections).map(([name, components]) => {
      if (!components) {
        return [name, components];
      }

      const kept = components.filter(component => !COUPON_COMPONENT_NAMES.includes(component.name));

      return [name, kept.length > 0 ? kept : null];
    }),
  ) as SectionMap;
